import { SEVERITY_LABELS } from '@fabric-ui/components';
import { FilterTypes, mapAliasesAndMetrics, QueryKey } from '@fabric-ui/utils';
import { UseQueryOptions } from '@tanstack/react-query';
import { EntityProjectionName, entityProjectionNames } from '@utils/entityProjectionNames';
import { field } from '@utils/filter';
import { addMinutes, format, isValid } from 'date-fns';
import { DwQueryRequest } from '../CustomDashboards/types';

export const RISK_MASS_DIMS = {
  LOW: 'low_active_findings_risk_mass_granular',
  MEDIUM: 'medium_active_findings_risk_mass_granular',
  HIGH: 'high_active_findings_risk_mass_granular',
  CRITICAL: 'critical_active_findings_risk_mass_granular',
  TOTAL: 'active_findings_risk_mass_granular',
} as const;

export const RISK_SCORE_MASS_DIMS = {
  LOW: 'low_active_findings_risk_score_mass_granular',
  MEDIUM: 'medium_active_findings_risk_score_mass_granular',
  HIGH: 'high_active_findings_risk_score_mass_granular',
  CRITICAL: 'critical_active_findings_risk_score_mass_granular',
  TOTAL: 'active_findings_risk_score_mass_granular',
} as const;

export const getRiskMassDims = (isNewRiskScoreEnabled = false) => (isNewRiskScoreEnabled ? RISK_SCORE_MASS_DIMS : RISK_MASS_DIMS);

export const entitySearchFields: Partial<Record<EntityProjectionName, string[]>> = {
  [entityProjectionNames.Ticket]: ['ticket.title', 'ticket._key'],
  [entityProjectionNames.Asset]: ['asset.name', 'asset._key'],
  [entityProjectionNames.Incident]: ['incident.title', 'incident._key'],
  [entityProjectionNames.Alert]: ['alert.title', 'alert._key'],
  [entityProjectionNames.Finding]: ['finding.title', 'finding.cve_id', 'component.name', 'component._key', 'asset.os', 'finding._key'],
  [entityProjectionNames.PolicyViolation]: ['policy_violation.title', 'policy_violation._key'],
  [entityProjectionNames.Exception]: ['ticket.title', 'exception._key'],
  [entityProjectionNames.ViolationTicket]: ['violation_ticket.title', 'violation_ticket._key'],
  [entityProjectionNames.IdentityFinding]: ['identity_finding.title', 'identity_finding._key'],
  [entityProjectionNames.User]: ['user.user_name', 'user._key'],
  [entityProjectionNames.Tenant]: ['tenant.user_name', 'tenant._key'],
  [entityProjectionNames.Identity]: ['identity.user_name', 'identity._key'],
};

export const formatAsUTC = (value, formatWithTime = false, keepSlaWithoutFormat = false, dateFormat = 'MMM dd, yyyy') => {
  const utcTime = addMinutes(new Date(value), new Date(value).getTimezoneOffset());
  const date = new Date(value);
  return formatWithTime
    ? (keepSlaWithoutFormat ? date : utcTime).toLocaleString()
    : format(keepSlaWithoutFormat ? date : utcTime, dateFormat);
};

export const getFieldLabel = ({ field, fieldMap, projectionName, aggProjs }) =>
  fieldMap[field]
    ? fieldMap[field].group === aggProjs[projectionName]?.projDisplayName
      ? fieldMap[field].displayName
      : fieldMap[field].title
    : field;

export const actionCallback =
  (onSuccess, queryClient) =>
  (...args) => {
    onSuccess(...args);
    queryClient.resetQueries({ queryKey: [QueryKey.TicketInfo] });
  };

export const StepsEnum = {
  NONE: 0,
  NEW: 5,
  DISCOVERED: 6,
  OPENED: 10,
  OPEN: 10,
  REOPEN: 11,
  ACKNOWLEDGED: 20,
  IN_PROGRESS: 21,
  REMEDIATED: 30,
  DISPUTED: 40,
  NOT_CONFIRMED: 50,
  CONFIRMED: 60,
  AUTO_CLOSED: 61,
  FALSE_POSITIVE: 62,
  RISK_ACCEPTED: 63,
};

export const STATUS_LABELS = {
  [StepsEnum.NONE]: 'None',
  [StepsEnum.NEW]: 'New',
  [StepsEnum.DISCOVERED]: 'Discovered',
  [StepsEnum.OPENED]: 'Opened',
  [StepsEnum.OPEN]: 'Open',
  [StepsEnum.REOPEN]: 'Reopen',
  [StepsEnum.ACKNOWLEDGED]: 'Acknowledged',
  [StepsEnum.IN_PROGRESS]: 'In Progress',
  [StepsEnum.REMEDIATED]: 'Remediated',
  [StepsEnum.NOT_CONFIRMED]: 'Not Confirmed',
  [StepsEnum.CONFIRMED]: 'Confirmed',
  [StepsEnum.AUTO_CLOSED]: 'Auto Closed',
  [StepsEnum.FALSE_POSITIVE]: 'False Positive',
  [StepsEnum.RISK_ACCEPTED]: 'Risk Accepted',
};

export const ORDERED_TICKET_STATUS = [
  STATUS_LABELS[StepsEnum.NEW],
  STATUS_LABELS[StepsEnum.OPENED],
  STATUS_LABELS[StepsEnum.OPEN],
  STATUS_LABELS[StepsEnum.REOPEN],
  STATUS_LABELS[StepsEnum.ACKNOWLEDGED],
  STATUS_LABELS[StepsEnum.IN_PROGRESS],
  STATUS_LABELS[StepsEnum.REMEDIATED],
  STATUS_LABELS[StepsEnum.CONFIRMED],
  STATUS_LABELS[StepsEnum.NOT_CONFIRMED],
  STATUS_LABELS[StepsEnum.AUTO_CLOSED],
  STATUS_LABELS[StepsEnum.FALSE_POSITIVE],
  STATUS_LABELS[StepsEnum.RISK_ACCEPTED],
];

export const explodeFields = () => ({
  [FilterTypes.AssetV2]: { select: 'asset._key', group: 'asset._key' },
  [FilterTypes.AssetTypeV2]: { select: 'asset.type', group: 'asset.type' },
  [FilterTypes.TagsV2]: { select: 'EXPLODE(ticket.tags) as tag', group: 'tag' },
});

export const drawerWidth = 1100;

const sortedSeverities = [
  SEVERITY_LABELS.CRITICAL,
  SEVERITY_LABELS.HIGH,
  SEVERITY_LABELS.MEDIUM,
  SEVERITY_LABELS.LOW,
  SEVERITY_LABELS.INFO,
];

export const fieldPurposeMap = {
  // TODO change to Field names
  uber_findings: {
    sources: 'Sources',
    type: 'Type',
    title: 'Title',
    severity: 'Severity',
    score: 'Severity score',
    state: 'State',
  },
  uber_assets: {
    sources: 'Sources',
    type: 'Type',
    title: 'Name',
    score: 'Risk Score',
    state: 'State',
  },
  tickets: {
    sources: 'Sources',
    type: 'Type',
    title: 'Title',
    severity: 'Severity',
    score: 'Severity score',
    state: 'State',
  },
  uber_global_vulnerabilities: {
    sources: 'Sources',
    type: 'Type',
    title: 'CVE',
    score: 'CVSS 3.1 Base Score',
    state: 'State',
  },
  uber_org_entities: {
    sources: 'Sources',
    type: 'Type',
    title: 'Name',
    state: 'State',
  },
  uber_components: {
    sources: 'Sources',
    type: 'Type',
    title: 'Name',
    state: 'State',
  },
  uber_component_instances: {
    sources: 'Sources',
    type: 'Type',
    title: 'ID',
    state: 'State',
  },
  uber_alerts: {
    sources: 'Sources',
    type: 'Type',
    title: 'Title',
    severity: 'Severity',
    score: 'Severity Score',
    state: 'State',
  },
  incidents: {
    sources: 'Sources',
    type: 'Type',
    title: 'Title',
    severity: 'Severity',
    score: 'Severity Score',
    state: 'State',
  },
  uber_alert_actors: {
    sources: 'Sources',
    type: 'Type',
    title: 'Title',
    state: 'State',
  },
  uber_applications: {
    sources: 'Sources',
    type: 'Type',
    title: 'Name',
    state: 'State',
  },
  uber_users: {
    sources: 'Sources',
    type: 'Type',
    title: ['Email', 'Name'],
    state: 'State',
  },
  uber_mitre_attacks: {
    sources: 'Sources',
    type: 'Type',
    title: 'Tactic',
    state: 'State',
  },
  default: {
    sources: 'Sources',
    type: 'Type',
    title: 'Name',
    state: 'State',
  },
};

export const groupByKeys = {
  assignee: {
    [entityProjectionNames.Ticket]: 'ticket.assignee_id',
    [entityProjectionNames.Incident]: 'incident.assignee_id',
    [entityProjectionNames.Asset]: 'asset.owner_id',
    sort: ({ title: a }, { title: b }) => {
      const keyToSortFirst = 'No Assignee';
      if ((a === keyToSortFirst) !== (b === keyToSortFirst)) {
        return a === keyToSortFirst ? -1 : 1;
      }
      return a > b ? 1 : a < b ? -1 : 0;
    },
  },
  sla: {
    [entityProjectionNames.Ticket]: 'ticket.sla',
    [entityProjectionNames.Incident]: 'incident.sla',
    sort: ({ title: a }, { title: b }) => {
      const keyToSortFirst = 'No SLA';
      if ((a === keyToSortFirst) !== (b === keyToSortFirst)) {
        return a === keyToSortFirst ? 1 : -1;
      }
      return +new Date(a) > +new Date(b) ? 1 : +new Date(a) < +new Date(b) ? -1 : 0;
    },
    formatter: v => (isValid(new Date(v)) ? formatAsUTC(v) : 'No SLA'),
  },
  avalorStatus: {
    [entityProjectionNames.Ticket]: 'ticket.current_status.name',
    [entityProjectionNames.Incident]: 'incident.current_status.name',
    sort: ({ title: a }, { title: b }) => {
      const statuses = ORDERED_TICKET_STATUS.map(v => v);
      return statuses.indexOf(a) - statuses.indexOf(b);
    },
    formatter: v => STATUS_LABELS[StepsEnum[v]],
  },
  firstSeen: {
    [entityProjectionNames.Ticket]: 'ticket.first_seen',
    [entityProjectionNames.Asset]: 'asset.first_seen',
    sort: ({ title: a }, { title: b }) => (+new Date(a) > +new Date(b) ? 1 : +new Date(a) < +new Date(b) ? -1 : 0),
    formatter: v => (v ? format(new Date(v), 'MMM dd, yyyy') : 'Unknown'),
  },
  created: {
    [entityProjectionNames.Ticket]: 'ticket.created',
    [entityProjectionNames.Incident]: 'incident.created',
    [entityProjectionNames.Asset]: 'asset.created',
    sort: ({ title: a }, { title: b }) => (+new Date(a) > +new Date(b) ? 1 : +new Date(a) < +new Date(b) ? -1 : 0),
    formatter: v => (v ? format(new Date(v), 'MMM dd, yyyy') : 'Unknown'),
  },
  severity: {
    [entityProjectionNames.Ticket]: 'ticket.severity',
    [entityProjectionNames.Incident]: 'incident.severity',
    sort: ({ title: a }, { title: b }) => sortedSeverities.indexOf(a) - sortedSeverities.indexOf(b),
    formatter: v => SEVERITY_LABELS[v],
  },
  type: {
    [entityProjectionNames.Ticket]: 'ticket.type',
    [entityProjectionNames.Incident]: 'incident.type',
    [entityProjectionNames.Asset]: 'asset.type',
    sort: ({ title: a }, { title: b }) => a - b,
  },
};

export const groupByKeysQueryProjection = (projectionName: string) => ({
  [groupByKeys.assignee?.[projectionName]]: `${groupByKeys.assignee?.[projectionName]} as val`,
  [groupByKeys.sla?.[projectionName]]: `DAY(${groupByKeys.sla?.[projectionName]}) AS val`,
  [groupByKeys.type?.[projectionName]]: `${groupByKeys.type?.[projectionName]} as val`,
  [groupByKeys.avalorStatus?.[projectionName]]: `${groupByKeys.avalorStatus?.[projectionName]} as val`,
  ...(projectionName === entityProjectionNames.Ticket || projectionName === entityProjectionNames.Asset
    ? { [groupByKeys.firstSeen?.[projectionName]]: `DAY(${groupByKeys.firstSeen?.[projectionName]}) as val` }
    : {}),
});

export const getInitialCustomHeadCells = ({ projectionName, extraRelationGroups, aggProjs, getType, isTableV2 }) => {
  const filteredFieldList = (
    extraRelationGroups?.includes('*')
      ? aggProjs[projectionName].fieldList.INTERACTIVE
      : aggProjs[projectionName].fieldList.INTERACTIVE.filter(({ group }) => extraRelationGroups.includes(group))
  ).filter(({ visibilityConfig }) => !visibilityConfig.config.hidden);
  return filteredFieldList.map(({ name, value, title }) => {
    if (isTableV2) {
      return {
        id: value,
        header: title || name,
        meta: {
          hidden: true,
          type: getType(name, value),
        },
      };
    }
    return {
      id: value,
      label: title || name,
      hidden: true,
      type: getType(name, value),
    };
  });
};

type RiskMassQueryConfig = {
  options: UseQueryOptions;
  queryObject: DwQueryRequest;
  onSuccess: (data: any) => any;
};

type GetRiskMassQueryObjectFn = ({
  ticketKey,
  entityPathAlias,
  projectionId,
}: {
  ticketKey: string;
  entityPathAlias: string;
  projectionId: { name: string; builtIn: boolean };
}) => RiskMassQueryConfig;
type ProjectionName = (typeof entityProjectionNames)[keyof typeof entityProjectionNames];

const getRiskMassQueryObject = ({ ticketKey, projectionId, entityPathAlias, isNewRiskScoreEnabled = false }): RiskMassQueryConfig => {
  const dims = getRiskMassDims(isNewRiskScoreEnabled);
  return {
    options: { queryKey: ticketKey, enabled: !!ticketKey, gcTime: 0 },
    queryObject: {
      projectionId,
      select: {
        dims: Object.values(dims).map(name => ({ name, alias: name })),
        metrics: [],
      },
      filter: field(`${entityPathAlias}._key`).str.equals(ticketKey),
      sorting: [],
      groupBy: [],
    },
    onSuccess: d => d[0],
  };
};

export const projectionRiskMassMap: (isNewRiskScoreEnabled?: boolean) => Partial<Record<ProjectionName, GetRiskMassQueryObjectFn>> = (
  isNewRiskScoreEnabled = false
) => ({
  [entityProjectionNames.Ticket]: ({ ticketKey, projectionId, entityPathAlias }) =>
    getRiskMassQueryObject({ ticketKey, projectionId, entityPathAlias, isNewRiskScoreEnabled }),
  [entityProjectionNames.Asset]: ({ ticketKey, projectionId, entityPathAlias }) =>
    getRiskMassQueryObject({ ticketKey, projectionId, entityPathAlias, isNewRiskScoreEnabled }),
});

const bulkPrefix = 'notification/ticket/bulk/';
export const apiUrls = {
  UPDATE_INTEGRATION: `${bulkPrefix}integration`,
  UPDATE_FIELDS: `${bulkPrefix}multi-fields`,
  CREATE_SINGLE_INTEGRATION: 'notification/ticket/integration',
  ADD_ATTACHMENT: 'notification/entity/metadata/attachment',
  UNLINK_INTEGRATION: 'notification/ticket/integration/unlink',
};

export const statusField = 'current_status.name';
export const filtersFieldToIgnore = [
  'tablePage',
  'orderBy',
  'search',
  'searchArray',
  'groupByKey',
  'tab',
  'ticketCategory',
  'ticket.detection_sources',
  'viewId',
  'activeEntityId',
  'investigationMode',
  'headCells',
  'expandCharts',
  'date',
  'dataSelectTypes',
  'fieldType',
  'isEditWidgetOpen',
  'isEditMode',
  'isCurrentTime',
  'activeProjName',
  'timeRange',
  'forceRender',
  'expressions',
  'dateGap',
  'dateObject',
  'tags',
  'historicalFilterMode',
];
export const getNestedFieldObj = (field, value) => ({ [field]: { name: value, timestamp: new Date().toISOString() } });

export const TABLE_AVALOR_KEY = 'avalor_key';
export const ASSET_TABLE_AVALOR_KEY = 'asset_avalor_key';

export const POLICY_VIOLATION_SEVERITY = 'policy_violation.severity';
export const POLICY_VIOLATION_SEVERITY_SCORE = 'policy_violation.severity_score';

export const ALERT_SEVERITY = 'alert.severity';
export const ALERT_SEVERITY_SCORE = 'alert.severity_score';

export const FINDING_SEVERITY = 'finding.severity';
export const FINDING_ORIGINAL_SEVERITY = 'finding.original_severity';
export const FINDING_ORIGINAL_SEVERITYSCORE = 'finding.original_severity_score';
export const FINDING_SEVERITY_SCORE = 'finding.severity_score';
export const FINDING_RISK_SCORE = 'finding.risk_score';
export const ASSET_RISK_SCORE = 'asset.risk_score';
export const TICKET_SEVERITY_SCORE = 'ticket.severity_score';
export const TICKET_RISK_SCORE = 'ticket.risk_score';
export const SCORE_EXPLANATION_FIELD = 'finding.score_factors';

export const EXCEPTION_REQUESTER_EMAIL = 'exception.requester_email';
export const EXCEPTION_REQUESTER_NAME = 'requester.name';
export const EXCEPTION_REVIEWER_EMAIL = 'exception.reviewer_email';
export const EXCEPTION_REVIEWER_NAME = 'reviewer.name';
export const EXCEPTION_STATUS_BUCKET_NAME = 'exception.status.status_bucket.name';
export const EXCEPTION_JUSTIFICATION_NAME = 'justification';
export const EXCEPTION_REASON_NAME = 'reason';
export const EXCEPTION_REQUESTED_SLA_NAME = 'requested_sla';

export const USER_NAME = "COALESCE(user.first_name + ' ' + user.last_name, user.user_name)";

export const SELECTED_ALL_ROWS_DISABLED_MSG = 'This action is disabled when all rows are selected';
export const fieldsWithEmptyStateSet = new Set(['alert.score_factors']);

export enum ExceptionType {
  FALSE_POSITIVE = 'false_positive',
  SLA_EXTENSION = 'SLA_EXTENSION',
  RISK_ACCEPTED = 'RISK_ACCEPTED',
}

export enum ExceptionScope {
  ENTITY = 'ENTITY',
  RULE = 'RULE',
}

export const getFindingScoreField = (isNewRiskScoreEnabled?: boolean): string =>
  isNewRiskScoreEnabled ? FINDING_RISK_SCORE : FINDING_SEVERITY_SCORE;

export const getAssetScoreField = (isNewRiskScoreEnabled?: boolean): string =>
  isNewRiskScoreEnabled ? ASSET_RISK_SCORE : mapAliasesAndMetrics.severityScore.metric;

export const getTicketScoreField = (isNewRiskScoreEnabled?: boolean): string =>
  isNewRiskScoreEnabled ? TICKET_RISK_SCORE : TICKET_SEVERITY_SCORE;
